import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button, Input } from '@artosera/ui';
import { centimesDepuisEuros } from '@artosera/shared';
import { facturationVenioApi, type ReglagesVenio } from '../lib/addons.js';
import { usePeut } from '../auth/usePeut.js';
import styles from '../styles/form.module.css';

export function FacturationVenio() {
  const peut = usePeut();
  const { t } = useTranslation('console');
  const requete = useQuery({
    queryKey: ['console', 'facturation-venio'],
    queryFn: facturationVenioApi.lire,
    enabled: peut('console:read-addon-sales'),
  });
  if (!peut('console:read-addon-sales')) return null;
  return (
    <section>
      <h2>{t('addons.venio.titre')}</h2>
      {requete.isPending ? <p>{t('addons.charger')}</p> : null}
      {requete.isError ? <p role="alert">{t('addons.venio.erreur')}</p> : null}
      {requete.data ? (
        <Formulaire
          key={requete.data.reglages?.revision ?? 0}
          initial={requete.data.reglages}
          modifiable={peut('console:manage-addon-billing')}
        />
      ) : null}
    </section>
  );
}
function Formulaire({
  initial,
  modifiable,
}: {
  initial: ReglagesVenio | null;
  modifiable: boolean;
}) {
  const { t } = useTranslation('console');
  const client = useQueryClient();
  const [invalide, setInvalide] = useState(false);
  const mutation = useMutation({
    mutationFn: facturationVenioApi.ecrire,
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['console', 'facturation-venio'] });
    },
  });
  const champs = [
    'raisonSociale',
    'formeJuridique',
    'siren',
    'siret',
    'tvaIntracom',
    'rcs',
    'courriel',
    'iban',
    'bic',
    'banque',
  ] as const;
  const adresse = ['ligne1', 'ligne2', 'codePostal', 'ville', 'pays'] as const;
  return (
    <form
      className={styles.champ}
      onSubmit={(event) => {
        event.preventDefault();
        if (!modifiable || mutation.isPending) return;
        const data = new FormData(event.currentTarget);
        const texte = (cle: string) => String(data.get(cle) ?? '').trim();
        const emetteur = Object.fromEntries(
          champs.filter((cle) => texte(cle)).map((cle) => [cle, texte(cle)]),
        );
        const coordonnees = Object.fromEntries(
          adresse.filter((cle) => texte(cle)).map((cle) => [cle, texte(cle)]),
        );
        try {
          const capital = texte('capitalCentimes');
          const saisie = {
            emetteur: {
              ...emetteur,
              adresse: coordonnees,
              ...(capital ? { capitalCentimes: centimesDepuisEuros(capital) } : {}),
            },
            delaiPaiementJours: Number(texte('delaiPaiementJours')),
            prefixeSerie: texte('prefixeSerie'),
            prefixeSerieAvoir: texte('prefixeSerieAvoir'),
            revision: initial?.revision ?? 0,
          } as ReglagesVenio;
          setInvalide(false);
          mutation.mutate(saisie);
        } catch {
          setInvalide(true);
        }
      }}
    >
      <fieldset disabled={!modifiable || mutation.isPending}>
        {champs.map((champ) => (
          <div key={champ}>
            <label htmlFor={`venio-${champ}`}>{t(`addons.venio.${champ}`)}</label>
            <Input
              id={`venio-${champ}`}
              name={champ}
              defaultValue={initial?.emetteur[champ] ?? ''}
              required={['raisonSociale', 'formeJuridique', 'siret', 'rcs'].includes(champ)}
              maxLength={254}
            />
          </div>
        ))}
        <label htmlFor="venio-capital">{t('addons.venio.capital')}</label>
        <Input
          id="venio-capital"
          name="capitalCentimes"
          required
          inputMode="decimal"
          defaultValue={
            initial?.emetteur.capitalCentimes === undefined
              ? ''
              : String(initial.emetteur.capitalCentimes / 100)
          }
        />
        {adresse.map((champ) => (
          <div key={champ}>
            <label htmlFor={`venio-${champ}`}>{t(`addons.facturation.${champ}`)}</label>
            <Input
              id={`venio-${champ}`}
              name={champ}
              defaultValue={initial?.emetteur.adresse[champ] ?? ''}
              required={champ !== 'ligne2'}
              maxLength={200}
            />
          </div>
        ))}
        <label htmlFor="venio-delai">{t('addons.venio.delai')}</label>
        <Input
          id="venio-delai"
          name="delaiPaiementJours"
          type="number"
          min={1}
          max={60}
          required
          defaultValue={initial?.delaiPaiementJours ?? 30}
        />
        <label htmlFor="venio-serie">{t('addons.venio.serie')}</label>
        <Input
          id="venio-serie"
          name="prefixeSerie"
          required
          defaultValue={initial?.prefixeSerie ?? 'AV'}
          pattern="[A-Z][A-Z0-9]{0,11}"
        />
        <label htmlFor="venio-serie-avoir">{t('addons.venio.serieAvoir')}</label>
        <Input
          id="venio-serie-avoir"
          name="prefixeSerieAvoir"
          required
          defaultValue={initial?.prefixeSerieAvoir ?? 'AVA'}
          pattern="[A-Z][A-Z0-9]{0,11}"
        />
        {modifiable ? (
          <Button type="submit" disabled={mutation.isPending}>
            {t('addons.venio.enregistrer')}
          </Button>
        ) : null}
      </fieldset>
      {mutation.isError || invalide ? <p role="alert">{t('addons.venio.erreur')}</p> : null}
      {mutation.isSuccess ? <p role="status">{t('addons.venio.enregistre')}</p> : null}
    </form>
  );
}
